import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Send, Smile, Paperclip, FileText, X, Mic, Square, Maximize2, Minimize2 } from "lucide-react";
import { ulid } from "ulid";
import { toast } from "sonner";
import { trpc } from "@/trpc";
import UserContext from "@/contexts/UserContext";
import TemplateMessageFlow from "../TemplateMessageFlow";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import AudioPlayer from "@/components/AudioPlayer";
import { convertWebmToMp3 } from "@/lib/convertWebmToMp3";
import posthog from "@/lib/posthog";
import { cn } from "@/lib/utils";
import type { OptimisticMessage, useOptimisticMessages } from "./useOptimisticMessages";

type MediaPreview = {
  file: File;
  previewUrl: string;
  type: "image" | "video" | "document" | "audio";
};

export type ComposerLeadData = {
  name?: string;
  phone?: string;
  email?: string;
};

export type ComposerConversationData = {
  lastCustomerMessageAt?: string;
  wabaId?: string;
  whatsappConnectionId?: string;
} | null;

type Props = {
  merchantId: string;
  leadId: string;
  leadData?: ComposerLeadData;
  conversationData?: ComposerConversationData;
  refetchTimeline: () => Promise<unknown>;
  refetchConversation: () => void;
  optimistic: ReturnType<typeof useOptimisticMessages>;
  posthogSurface: "inbox" | "tablero";
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/3gpp"];
const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_VIDEO_SIZE = 16 * 1024 * 1024; // 16 MB
const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024; // 100 MB (WhatsApp document limit)
const MAX_AUDIO_SIZE = 16 * 1024 * 1024; // 16 MB
const MAX_RECORDING_DURATION = 120; // 2 minutes in seconds
// The textarea grows with its content up to this height, then scrolls and offers the expand toggle.
const MAX_COMPOSER_HEIGHT = 168;

const MessageComposer = ({
  merchantId,
  leadId,
  leadData,
  conversationData,
  refetchTimeline,
  refetchConversation,
  optimistic,
  posthogSurface,
}: Props) => {
  const { email: userEmail } = useContext(UserContext);
  const [newMessage, setNewMessage] = useState("");
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const sendMessageMutation = trpc.conversations.sendMessage.useMutation();
  const getUploadUrlMutation = trpc.conversations.getUploadUrl.useMutation();

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string; type?: "image" | "video" | "document" } => {
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);
      const isDocument = ALLOWED_DOCUMENT_TYPES.includes(file.type);

      if (!isImage && !isVideo && !isDocument) {
        return {
          valid: false,
          error:
            "Tipo de archivo no soportado. Solo se permiten imágenes (JPEG, PNG, WebP), videos (MP4, 3GPP) o documentos (PDF, DOC, DOCX).",
        };
      }

      if (isImage && file.size > MAX_IMAGE_SIZE) {
        return {
          valid: false,
          error: "La imagen es demasiado grande. El tamaño máximo es 5 MB.",
        };
      }

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        return {
          valid: false,
          error: "El video es demasiado grande. El tamaño máximo es 16 MB.",
        };
      }

      if (isDocument && file.size > MAX_DOCUMENT_SIZE) {
        return {
          valid: false,
          error: "El documento es demasiado grande. El tamaño máximo es 100 MB.",
        };
      }

      return {
        valid: true,
        type: isImage ? "image" : isVideo ? "video" : "document",
      };
    },
    []
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error("Error al seleccionar archivo", {
          description: validation.error,
          duration: 5000,
        });
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setMediaPreview({
        file,
        previewUrl,
        type: validation.type!,
      });

      // Reset the input so the same file can be selected again
      event.target.value = "";
    },
    [validateFile]
  );

  const handleRemoveMedia = useCallback(() => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview.previewUrl);
      setMediaPreview(null);
    }
  }, [mediaPreview]);

  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Prefer WebM — decodeAudioData handles it reliably for our MP3 conversion.
  // Avoid audio/mp4: Chrome's MediaRecorder MP4 variant can't be decoded by Web Audio API.
  const getRecordingMimeType = (): string => {
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
    if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
    if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
    return "audio/webm";
  };

  const handleStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });

      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        if (blob.size > MAX_AUDIO_SIZE) {
          toast.error("Nota de voz demasiado larga", {
            description: "El tamaño máximo es 16 MB. Intenta grabar un mensaje más corto.",
            duration: 5000,
          });
          setIsRecording(false);
          setRecordingDuration(0);
          return;
        }

        let file: File;
        let previewUrl: string;

        // Always convert to MP3 for WhatsApp compatibility.
        // Chrome's audio/mp4 and audio/webm aren't reliably accepted by WhatsApp.
        // OGG (Firefox) works but MP3 is universally supported — just convert everything.
        try {
          file = await convertWebmToMp3(blob);
          previewUrl = URL.createObjectURL(file);
        } catch (error) {
          console.error("Error converting audio to MP3:", error);
          toast.error("Error al procesar la nota de voz", {
            description: "No se pudo convertir el audio. Intenta de nuevo.",
            duration: 5000,
          });
          setIsRecording(false);
          setRecordingDuration(0);
          return;
        }

        setMediaPreview({ file, previewUrl, type: "audio" });
        setIsRecording(false);
        setRecordingDuration(0);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev + 1 >= MAX_RECORDING_DURATION) {
            mediaRecorderRef.current?.stop();
            return prev + 1;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("No se pudo acceder al micrófono", {
        description: "Verifica los permisos del navegador.",
        duration: 5000,
      });
    }
  }, []);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleCancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingDuration(0);
  }, []);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const showExpanded = isExpanded && !isRecording;

  // Auto-grow: size the textarea to its content, capped at MAX_COMPOSER_HEIGHT.
  // Expanded mode hands sizing back to the flex layout.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (showExpanded) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT)}px`;
    setIsOverflowing(el.scrollHeight > MAX_COMPOSER_HEIGHT);
  }, [newMessage, showExpanded, isRecording]);

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
    textareaRef.current?.focus();
  };

  const formatRecordingTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Check if we can send a message (must have a recent customer message within 24 hours)
  const isMessageTimeoutExpired = useMemo(() => {
    if (!conversationData?.lastCustomerMessageAt) return true;

    const lastCustomerMessageTime = new Date(conversationData.lastCustomerMessageAt).getTime();
    const now = Date.now();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;

    return now - lastCustomerMessageTime > twentyFourHoursInMs;
  }, [conversationData?.lastCustomerMessageAt]);

  const handleSendMessage = async () => {
    const hasText = newMessage.trim().length > 0;
    const hasMedia = mediaPreview !== null;

    if ((!hasText && !hasMedia) || sendMessageMutation.isLoading || isMessageTimeoutExpired) return;

    const optimisticId = ulid();
    const now = new Date().toISOString();

    if (hasMedia) {
      // For media messages, we need to upload first, so show uploading state
      setIsUploading(true);

      try {
        // Strip codec params (e.g. "audio/webm;codecs=opus" -> "audio/webm")
        // to avoid S3 presigned URL signature mismatches from browser normalization
        const baseContentType = mediaPreview.file.type.split(";")[0].trim();

        const { uploadUrl, s3Key, mediaType } = await getUploadUrlMutation.mutateAsync({
          merchantId,
          leadId,
          fileName: mediaPreview.file.name,
          contentType: baseContentType,
        });

        // Upload directly to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: mediaPreview.file,
          headers: {
            "Content-Type": baseContentType,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file to S3");
        }

        setIsUploading(false);

        posthog.capture("media message sent", {
          media_type: mediaType,
          merchant_id: merchantId,
          lead_id: leadId,
          surface: posthogSurface,
        });

        // Create optimistic message for media
        const optimisticMessage: OptimisticMessage = {
          type: "message",
          timestamp: now,
          message: {
            id: optimisticId,
            conversationId: "",
            content: "",
            source: "human_agent",
            sourceId: userEmail,
            createdAt: now,
            lastStatus: "pending",
            isOptimistic: true,
            ...(mediaType === "image" ? { image: { url: mediaPreview.previewUrl } } : {}),
            ...(mediaType === "video" ? { video: { url: mediaPreview.previewUrl } } : {}),
            ...(mediaType === "document" ? { document: { url: mediaPreview.previewUrl } } : {}),
            ...(mediaType === "audio" ? { audio: { url: mediaPreview.previewUrl } } : {}),
          },
        };

        optimistic.add(optimisticMessage);
        handleRemoveMedia();

        // Send the media message (fire and don't await)
        sendMessageMutation.mutate(
          {
            merchantId,
            leadId,
            content: "",
            mediaType,
            mediaS3Key: s3Key,
          },
          {
            onSuccess: async (result) => {
              // Refetch first, then remove optimistic message to avoid gap
              await refetchTimeline();
              optimistic.remove(optimisticId);
              if (!result.success) {
                toast.warning("Error al enviar mensaje", {
                  description: "El mensaje no se pudo enviar a WhatsApp. Puedes reintentar.",
                  duration: 5000,
                });
              }
            },
            onError: () => {
              // Mark optimistic message as failed
              optimistic.markFailed(optimisticId);
              toast.error("Error al enviar mensaje", {
                description: "No se pudo enviar el mensaje. Por favor, intenta de nuevo.",
                duration: 5000,
              });
            },
          }
        );
      } catch (error) {
        console.error("Error uploading media:", error);
        setIsUploading(false);
        toast.error("Error al subir archivo", {
          description: "No se pudo subir el archivo. Por favor, intenta de nuevo.",
          duration: 5000,
        });
      }
    } else {
      // Text message - optimistic update immediately
      const messageContent = newMessage.trim();

      const optimisticMessage: OptimisticMessage = {
        type: "message",
        timestamp: now,
        message: {
          id: optimisticId,
          conversationId: "",
          content: messageContent,
          source: "human_agent",
          sourceId: userEmail,
          createdAt: now,
          lastStatus: "pending",
          isOptimistic: true,
        },
      };

      optimistic.add(optimisticMessage);
      setNewMessage("");
      setIsExpanded(false);

      posthog.capture("message sent", {
        merchant_id: merchantId,
        lead_id: leadId,
        surface: posthogSurface,
      });

      // Fire mutation without awaiting
      sendMessageMutation.mutate(
        {
          merchantId,
          leadId,
          content: messageContent,
        },
        {
          onSuccess: async (result) => {
            // Refetch first, then remove optimistic message to avoid gap
            await refetchTimeline();
            optimistic.remove(optimisticId);
            if (!result.success) {
              toast.warning("Error al enviar mensaje", {
                description: "El mensaje no se pudo enviar a WhatsApp. Puedes reintentar.",
                duration: 5000,
              });
            }
          },
          onError: () => {
            // Mark optimistic message as failed
            optimistic.markFailed(optimisticId);
            toast.error("Error al enviar mensaje", {
              description: "No se pudo enviar el mensaje. Por favor, intenta de nuevo.",
              duration: 5000,
            });
          },
        }
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && isExpanded) {
      e.preventDefault();
      setIsExpanded(false);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emoji: string) => {
    setNewMessage((prev) => prev + emoji);
  };

  if (isMessageTimeoutExpired) {
    return (
      <TemplateMessageFlow
        merchantId={merchantId}
        leadId={leadId}
        leadData={leadData}
        wabaId={conversationData?.wabaId}
        whatsappConnectionId={conversationData?.whatsappConnectionId}
        onTemplateSent={() => {
          refetchTimeline();
          refetchConversation();
        }}
        onOptimisticMessage={optimistic.add}
        onRemoveOptimisticMessage={optimistic.remove}
        onMarkOptimisticFailed={optimistic.markFailed}
      />
    );
  }

  return (
    <div
      className={cn(
        "sticky bottom-0 border-t border-gray-200 bg-white p-2 md:p-4",
        // Out-grow the timeline (flex-1) so the composer takes over the chat column.
        showExpanded && "flex min-h-0 flex-[999_1_0%] flex-col"
      )}
    >
      <div className={cn("flex flex-col gap-3", showExpanded && "min-h-0 flex-1")}>
        {isMessageTimeoutExpired && (
          <Alert status="warning">
            <p className="text-sm">
              No puedes enviar mensajes porque han pasado más de 24 horas desde el último mensaje
              del cliente.
            </p>
          </Alert>
        )}
        {mediaPreview && (
          <div className="relative inline-block self-start">
            {mediaPreview.type === "image" ? (
              <img
                src={mediaPreview.previewUrl}
                alt="Preview"
                className="max-h-[120px] max-w-[200px] rounded-md object-cover"
              />
            ) : mediaPreview.type === "video" ? (
              <video
                src={mediaPreview.previewUrl}
                className="max-h-[120px] max-w-[200px] rounded-md object-cover"
              />
            ) : mediaPreview.type === "audio" ? (
              <div className="w-full min-w-[200px]">
                <AudioPlayer audioUrl={mediaPreview.previewUrl} />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 p-3">
                <FileText size={24} />
                <p className="max-w-[200px] truncate text-sm">
                  {mediaPreview.file.name}
                </p>
              </div>
            )}
            <button
              className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              onClick={handleRemoveMedia}
              aria-label="Eliminar archivo"
            >
              <X className="size-3" />
            </button>
            {isUploading && (
              <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center rounded-md bg-black/50">
                <Spinner className="text-white" />
              </div>
            )}
          </div>
        )}
        {isRecording ? (
          <div className="flex min-h-[44px] items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 md:min-h-[80px]">
            <div className="size-3 animate-pulse rounded-full bg-red-500" />
            <span className="text-sm font-medium text-red-700">
              {formatRecordingTime(recordingDuration)}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancelRecording}
                className="flex min-h-11 items-center gap-1 rounded-md px-3 text-sm text-gray-600 hover:bg-red-100"
              >
                <X className="size-4" />
                <span>Cancelar</span>
              </button>
              <button
                onClick={handleStopRecording}
                className="flex min-h-11 items-center gap-1 rounded-md bg-red-600 px-3 text-sm text-white hover:bg-red-700"
              >
                <Square className="size-3" />
                <span>Detener</span>
              </button>
            </div>
          </div>
        ) : (
          <div className={cn("relative", showExpanded && "flex min-h-0 flex-1")}>
            <textarea
              ref={textareaRef}
              placeholder="Escribe tu mensaje..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              className={cn(
                "min-h-[44px] w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary disabled:opacity-50 md:min-h-[80px] md:text-sm",
                (isOverflowing || showExpanded) && "pr-10",
                showExpanded && "min-h-0 flex-1 md:min-h-0"
              )}
              disabled={isMessageTimeoutExpired || mediaPreview !== null}
            />
            {(isOverflowing || showExpanded) && (
              <button
                type="button"
                aria-label={showExpanded ? "Contraer editor" : "Expandir editor"}
                title={showExpanded ? "Contraer (Esc)" : "Expandir"}
                onClick={handleToggleExpanded}
                className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-md bg-white/90 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                {showExpanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/png,image/webp,video/mp4,video/3gpp,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/msword,.doc"
              style={{ display: "none" }}
            />
            <button
              aria-label="Adjuntar archivo"
              className="flex size-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              onClick={handleAttachmentClick}
              disabled={isMessageTimeoutExpired || mediaPreview !== null || isRecording}
            >
              <Paperclip className="size-4" />
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  aria-label="Agregar emoji"
                  className="flex size-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                  disabled={isMessageTimeoutExpired || mediaPreview !== null || isRecording}
                >
                  <Smile className="size-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto" side="top">
                <div className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {["😊", "👍", "❤️", "😂", "😍", "🎉", "🔥", "💯", "👏", "🙏", "😎", "🤔"].map(
                      (emoji) => (
                        <button
                          key={emoji}
                          className="min-w-0 p-1 text-lg hover:bg-gray-100 rounded"
                          onClick={() => handleEmojiClick(emoji)}
                        >
                          {emoji}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <button
              aria-label="Grabar nota de voz"
              className="flex size-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              onClick={handleStartRecording}
              disabled={isMessageTimeoutExpired || mediaPreview !== null || isRecording}
            >
              <Mic className="size-4" />
            </button>
          </div>
          <Button
            size="sm"
            className="rounded-full bg-green-600 text-white hover:bg-green-700"
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !mediaPreview) || isMessageTimeoutExpired || isRecording}
          >
            {/* The label is wrapped so the icon swap inserts before an element,
                never before a bare text node an extension may have reparented. */}
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                <span>Subiendo</span>
              </>
            ) : (
              <>
                <Send className="size-4" />
                <span>Enviar</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;
